import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaCheckCircle, FaClock, FaFileExport, FaMoneyBillWave, FaSearch, FaTimes, FaTruck } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import Select from "react-select";
import { supplierActions } from "../../sagas/supplierSlice";
import { formatPrice } from "../../helpers/utility";
const FournisseurEcheance = () => {
  const { t } = useTranslation();
  const { loading, error, echeances } = useSelector(state => state.reglementsFournisseur);
  const { suppliers } = useSelector(state => state.suppliers);

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [filters, setFilters] = useState({
    fournisseurId: null,
    dateFrom: formatDate(startOfMonth),
    dateTo: formatDate(endOfMonth),
    statut: null,
    modeReglement: ""
  });
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(supplierActions.search({
      query: ""
    }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(reglementsFournisseurActions.echeanceFournisseur(filters));
  }, [dispatch, filters]);

  const handleSearch = (e) => {
    e && e.preventDefault();
    dispatch(reglementsFournisseurActions.echeanceFournisseur(filters));
  };

  const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

  const getStatutBadge = (statut) => {
    const badges = {
      'EN_ATTENTE': { color: 'info', icon: FaClock },
      'DECAISSE': { color: 'success', icon: FaCheckCircle },
      'IMPAYE': { color: 'danger', icon: FaTimes },
      'ANNULE': { color: 'warning', icon: FaTimes }
    };

    const badge = badges[statut] || { color: 'secondary', icon: FaClock };
    const Icon = badge.icon;

    return (
      <Badge color={badge.color}>
        <Icon className="me-1" size="12" />
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  const handleExport = () => {
    // Logic to handle export of mouvements
    // This could be a call to a service that generates a report or downloads data
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaCheck className="text-primary me-2" />
                {t('reglement.echeancesFournisseur')}
              </h4>
              <div className="d-flex align-items-center">
                <Button
                  color="success"
                  onClick={() => handleExport()}
                  className="me-2"
                >
                  <FaFileExport className="me-2" />
                  {t('export.salesExport', 'Export Etats')}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Statistics Cards */}
      <Row className="mb-4">
        {/* Statistics Cards or Loading Spinner */}
        {loading ? (
          <Col md={12} className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
            <div className="mt-2 text-muted">{t('common.loadingData')}</div>
          </Col>
        ) : echeances && Array.isArray(echeances) && (
          <>
            <Col md={3}>
              <Card className="shadow-sm text-center">
                <CardBody>
                  <div className="fw-bold fs-4">{echeances.length}</div>
                  <div className="text-muted">{t('reglement.totalEcheance', 'Total échéances')}</div>
                  <div className="mt-2 text-primary fw-bold">
                    {formatPrice(echeances.reduce((sum, e) => sum + (Number(e.montantRegle) || 0), 0))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm text-center">
                <CardBody>
                  <div className="fw-bold fs-4">
                    {echeances.filter(e => e.statut === 'EN_ATTENTE').length}
                  </div>
                  <div className="text-muted">{t('reglement.totalEnAttente', 'Total en attente')}</div>
                  <div className="mt-2 text-info fw-bold">
                    {formatPrice(echeances.filter(e => e.statut === 'EN_ATTENTE').reduce((sum, e) => sum + (Number(e.montantRegle) || 0), 0))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm text-center">
                <CardBody>
                  <div className="fw-bold fs-4">
                    {echeances.filter(e => e.statut === 'DECAISSE').length}
                  </div>
                  <div className="text-muted">{t('reglement.totalDecaisse', 'Total décaisse')}</div>
                  <div className="mt-2 text-success fw-bold">
                    {formatPrice(echeances.filter(e => e.statut === 'DECAISSE').reduce((sum, e) => sum + (Number(e.montantRegle) || 0), 0))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm text-center">
                <CardBody>
                  <div className="fw-bold fs-4">
                    {echeances.filter(e => e.statut === 'IMPAYE').length}
                  </div>
                  <div className="text-muted">{t('reglement.totalImpaye', 'Total impayé')}</div>
                  <div className="mt-2 text-danger fw-bold">
                    {formatPrice(echeances.filter(e => e.statut === 'IMPAYE').reduce((sum, e) => sum + (Number(e.montantRegle) || 0), 0))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </>
        )}
      </Row>
      <Row className="mb-3">
        <Col>
          <Card className="shadow-sm">
            <CardBody>
              <Form inline onSubmit={handleSearch} className="row g-3 ">
                <Col md={2}>
                  <Label htmlFor="fournisseurId">{t('reglement.fournisseur')}:</Label>
                  <Select
                    id="fournisseurId"
                    options={fournisseurOptions}
                    value={fournisseurOptions.find(option => option.value === filters.fournisseurId)}
                    onChange={(selectedOption) => setFilters(f => ({ ...f, fournisseurId: selectedOption?.value ?? null }))}
                    placeholder={t('reglement.selectFournisseur')}
                    isSearchable
                    isClearable
                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="dateFrom">Date début</Label>
                    <Input
                      type="date"
                      id="dateFrom"
                      value={filters.dateFrom}
                      onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                    />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="dateTo">Date fin</Label>
                    <Input
                      type="date"
                      id="dateTo"
                      value={filters.dateTo}
                      onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                    />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <Label htmlFor="statut">{t('reglement.statut')}:</Label>
                  <Input
                    type="select"
                    value={filters.statut}
                    onChange={(e) => setFilters(f => ({ ...f, statut: e.target.value }))}
                  >
                    <option value="">{t('reglement.allStatuts')}</option>
                    <option value="EN_ATTENTE">{t('reglement.enAttente')}</option>
                    <option value="DECAISSE">{t('reglement.decaisse')}</option>
                    <option value="IMPAYE">{t('reglement.impaye')}</option>
                    <option value="ANNULE">{t('reglement.annule')}</option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Label htmlFor="modeReglement">{t('reglement.modeReglement', 'Mode')}:</Label>
                  <Input
                    id="modeReglement"
                    type="select"
                    value={filters.modeReglement}
                    onChange={(e) => setFilters(f => ({ ...f, modeReglement: e.target.value }))}
                  >
                    <option value="">{t('common.all', 'Tous')}</option>
                    <option value="CHEQUE">{t('reglement.cheque', 'Cheque')}</option>
                    <option value="TRAITE">{t('reglement.traite', 'Traite')}</option>
                  </Input>
                </Col>
                <Col md={1} >
                  <Button color="primary" type="submit" className="mt-4" size="sm">
                    <FaSearch className="me-2" />
                  </Button>
                </Col>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card className="shadow-sm">
            <CardBody>
              {loading ? (
                <div className="text-center my-4"><Spinner color="primary" /></div>
              ) : error ? (
                <div className="text-danger">Erreur lors du chargement des échéances.</div>
              ) : (
                <Table responsive bordered hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Référence</th>
                      <th>N° Chèque</th>
                      <th>Date opération</th>
                      <th>{t('reglement.modeReglement', 'Mode')}</th>
                      <th>Banque</th>
                      <th>Fournisseur</th>
                      <th>Montant chèque</th>
                      <th>Statut</th>
                      <th>Date d'échéance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {echeances && echeances.length > 0 ? (
                      echeances.map((m, idx) => (
                        <tr key={idx}>
                          <td>{m.reference}</td>
                          <td>{m.numeroCheque}</td>
                          <td>{m.dateReglement}</td>
                          <td>{m.modeReglement === 'TRAITE' ? t('reglement.traite', 'Traite') : t('reglement.cheque', 'Cheque')}</td>
                          <td>{m.banque}</td>
                          <td>{m.codeFournisseur} / {m.nomFournisseur}</td>
                          <td>{m.montantRegle}</td>
                          <td>{getStatutBadge(m.statut)}</td>
                          <td>{m.dateEcheance}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-5">
                          <div className="d-flex flex-column align-items-center justify-content-center">
                            <span style={{ fontSize: '2rem', color: '#adb5bd' }}>
                              <FaTruck />
                            </span>
                            <div className="mt-2 text-muted">
                              {t('reglement.noData', 'Aucun échéance trouvé.')}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FournisseurEcheance;
